/**
 * AlertsWidget Component
 *
 * Dashboard widget displaying system alerts requiring admin attention.
 * Shows actionable items like pending approvals and expiring credentials.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-148 - Admin Dashboard (21 SP)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserCheck,
  FileText,
  CreditCard,
  Calendar,
  ShoppingBag,
  AlertCircle
} from 'lucide-react';
import LoadingSpinner from '../../common/LoadingSpinner';
import Badge from '../../common/Badge';
import EmptyState from '../../common/EmptyState';

export function AlertsWidget({ alerts, loading }) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Alerts</h2>
        <LoadingSpinner message="Loading alerts..." size="sm" />
      </div>
    );
  }

  if (!alerts) {
    return null;
  }

  const alertItems = [
    {
      icon: UserCheck,
      label: 'Pending guide approvals',
      count: alerts.pending_guide_approvals,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      path: '/admin/guide-approvals',
    },
    {
      icon: FileText,
      label: 'Guides pending requirements',
      count: alerts.guides_pending_requirements,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      path: '/admin/guide-approvals',
    },
    {
      icon: CreditCard,
      label: 'Expiring credentials (30d)',
      count: alerts.expiring_credentials_30d,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      path: '/admin/dashboard/expiring-credentials',
    },
    {
      icon: Calendar,
      label: 'Expired credentials',
      count: alerts.expired_credentials,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      path: '/admin/dashboard/expiring-credentials',
    },
    {
      icon: ShoppingBag,
      label: 'Pending bookings',
      count: alerts.pending_bookings,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      path: '/admin/bookings',
    },
  ];

  const activeAlerts = alertItems.filter(item => item.count > 0);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">System Alerts</h2>
        {alerts.total_alerts > 0 && (
          <Badge color="red">
            {alerts.total_alerts} total
          </Badge>
        )}
      </div>

      {activeAlerts.length === 0 ? (
        <EmptyState
          icon={AlertCircle}
          title="No alerts at this time"
        />
      ) : (
        <div className="space-y-3">
          {activeAlerts.map((item, index) => (
            <button
              key={index}
              onClick={() => navigate(item.path)}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${item.bgColor}`}>
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                </div>
                <span className="text-sm font-medium text-gray-700">
                  {item.label}
                </span>
              </div>
              <span className={`text-lg font-bold ${item.color}`}>
                {item.count}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default AlertsWidget;
